import accounting, { CurrencyFormat } from "accounting";

export type CurrencyCode = "USD" | "GBP" | "EUR" | "NONE";

type CurrencyDisplay = {
  symbol: string;
  code: CurrencyCode;
  label: string;
  shortLabel: string;
  precision: number;
  decimalSeparator: string;
  thousandSeparator: string;
  prefix?: string;
  suffix?: string;
  defaultFormat: string;
  formatAccounting: CurrencyFormat;
};

export const currencies: CurrencyDisplay[] = [
  {
    symbol: "$",
    code: "USD",
    label: "Dollar ($)",
    shortLabel: "USD ($)",
    precision: 2,
    decimalSeparator: ".",
    thousandSeparator: ",",
    prefix: "$",
    defaultFormat: "%s %v",
    formatAccounting: {
      pos: "%s %v ",
      neg: "(%s %v)",
      zero: "%s %v ",
    },
  },
  {
    symbol: "£",
    code: "GBP",
    label: "Pound (£)",
    shortLabel: "GBP (£)",
    precision: 2,
    decimalSeparator: ".",
    thousandSeparator: ",",
    prefix: "£ ",
    defaultFormat: "%s %v",
    formatAccounting: {
      pos: "%s %v ",
      neg: "(%s %v)",
      zero: "%s %v ",
    },
  },
  {
    symbol: "€",
    code: "EUR",
    label: "Euro (€)",
    shortLabel: "EUR (€)",
    precision: 2,
    decimalSeparator: ",",
    thousandSeparator: ".",
    suffix: " €",
    defaultFormat: "%v %s",
    formatAccounting: {
      pos: "%s %v ",
      neg: "%s (%v)",
      zero: "%s %v ",
    },
  },
  {
    symbol: " ",
    code: "NONE",
    label: "Amount",
    shortLabel: "Blank",
    precision: 2,
    decimalSeparator: ".",
    thousandSeparator: ",",
    defaultFormat: "%v",
    formatAccounting: {
      pos: "%v ",
      neg: "(%v)",
      zero: "%v ",
    },
  },
] as const;

let fallbackCurrency =
  currencies.find(
    (f) =>
      f.code === (localStorage.getItem("lastSetCurrencyCode") as CurrencyCode),
  ) || currencies[0];

export const getFallbackCurrency = () => fallbackCurrency;
export const setFallbackCurrency = (currencyCode?: CurrencyCode | null) => {
  if (!currencyCode) currencyCode = "USD";

  localStorage.setItem("lastSetCurrencyCode", currencyCode);

  fallbackCurrency =
    currencies.find((f) => f.code === currencyCode) || currencies[0];
};

export const formatMoney = (
  value: number | null | undefined,
  currencyCode?: CurrencyCode,
  options?: { use0ForInvalid?: boolean; formatStyle?: "accounting" },
) => {
  const currency = !currencyCode
    ? getFallbackCurrency()
    : currencies.find((f) => f.code === currencyCode) || currencies[0];

  const isInvalid = value === null || value === undefined || isNaN(value);
  const use0ForInvalid = options?.use0ForInvalid || false;

  if (isInvalid && !use0ForInvalid) return "";
  if (isInvalid) value = 0;

  // @ts-expect-error: accounting.formatMoney is overloaded
  return accounting.formatMoney(value!, {
    symbol: currency.symbol,
    decimal: currency.decimalSeparator,
    thousand: currency.thousandSeparator,
    precision: currency.precision,
    format:
      options?.formatStyle === "accounting"
        ? currency.formatAccounting
        : currency.defaultFormat,
  });
};

export const formatMoneyErase00Decimal = (
  value: number | null | undefined,
  currencyCode?: CurrencyCode,
) => {
  const currency = getFallbackCurrency();

  if (value === null || value === undefined || isNaN(value)) return "";

  return formatMoney(value, currencyCode).replace(
    currency.decimalSeparator + "00",
    "",
  );
};
